import { useApiClient } from '@brewardapp/api-client';
import { useStore } from '@breward/store';
import { Card, DATE_FORMAT } from '@breward/ui';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface FeedbackSectionProps {
  scoresheet: any;
  onChange: () => void;
  readOnlyParam?: boolean;
  readOnlyGrade?: boolean;
  isTabContent?: boolean;
}

interface CommentFormValues {
  comment: string;
}

const STAR_LABELS = [
  'Pages:ParticipantWeb.ScoresheetPage.feedback.star1',
  'Pages:ParticipantWeb.ScoresheetPage.feedback.star2',
  'Pages:ParticipantWeb.ScoresheetPage.feedback.star3',
  'Pages:ParticipantWeb.ScoresheetPage.feedback.star4',
  'Pages:ParticipantWeb.ScoresheetPage.feedback.star5',
  'Pages:ParticipantWeb.ScoresheetPage.feedback.star6',
];
const STAR_DEFAULTS = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent', 'Outstanding'];

const FeedbackSection = ({
  scoresheet,
  onChange,
  readOnlyParam = false,
  readOnlyGrade = false,
}: FeedbackSectionProps) => {
  const [grade, setGrade] = useState<number | undefined>(undefined);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [t] = useTranslation();
  const { register, handleSubmit, reset, watch } = useForm<CommentFormValues>();
  const showNotification = useStore((state) => state.showNotification);
  const userId = useStore((state) => state.userId);
  const params = useParams();

  const readOnly = scoresheet?.scoresheetType === 'certification' && !readOnlyParam;
  const gradeReadOnly = readOnly || readOnlyGrade;
  const judgeUserId = scoresheet?.judge?.data?.user?.data?.id || scoresheet?.judge?.user?.id || scoresheet?.judge?.userId;
  const isJudge = judgeUserId === userId;

  const api = useApiClient();
  const updateScoresheet = api.scoresheets.useUpdateScoresheetsFeedback();
  const createScoresheetComment = api.scoresheetComments.useCreateScoresheetComment();

  const getStarLabel = (index: number) => t(STAR_LABELS[index], STAR_DEFAULTS[index]);

  useEffect(() => {
    if (scoresheet?.grade !== undefined) {
      setGrade(scoresheet?.grade);
    }
  }, [scoresheet]);

  const onGradeSelect = (index: number) => {
    if (gradeReadOnly) return;
    setGrade(index);
    updateScoresheet.mutate(
      { id: scoresheet?.id, grade: index },
      {
        onSuccess: () => {
          showNotification({
            type: 'success',
            title: t('Notifications:JudgeWeb.scoresheetFeedbackSaved.title', 'Grade saved!'),
            message: t('Notifications:JudgeWeb.scoresheetFeedbackSaved.message', 'Your grade has been saved.'),
          });
          onChange();
        },
      },
    );
  };

  const commentValue = watch('comment');

  const onCommentSubmit = (values: CommentFormValues) => {
    if (!values?.comment?.trim()) return;
    createScoresheetComment.mutate(
      {
        sectionKey: 'main',
        comment: values?.comment,
        scoresheetId: scoresheet?.id || params?.scoresheetId,
      },
      {
        onSuccess: () => {
          reset();
          onChange?.();
        },
      },
    );
  };

  const comments = Array.isArray(scoresheet?.comments)
    ? scoresheet.comments
    : Array.isArray(scoresheet?.comments?.data)
      ? scoresheet.comments.data
      : [];

  const sectionComments = comments.filter(
    (c: any) => (c?.sectionKey ?? c?.section_key) === 'main',
  );

  return (
    <div className="space-y-4">
      {/* ── Grade Section ── */}
      <Card>
        <div className="col-span-12 space-y-4">
          <div className="space-y-3">
            <div>
              <p className="font-sans text-sm font-semibold uppercase tracking-widest text-primary-700 dark:text-primary-300 mb-1">
                {t('Pages:ParticipantWeb.ScoresheetPage.feedbackLabel', 'Rate this scoresheet')}
              </p>
              <p className="font-sans text-sm text-content-500 dark:text-content-400 h-5">
                {hoveredStar !== null
                  ? getStarLabel(hoveredStar)
                  : (grade !== undefined && grade !== null)
                    ? getStarLabel(grade)
                    : gradeReadOnly
                      ? t('Pages:ParticipantWeb.ScoresheetPage.feedbackNotRatedYet', 'No rating has been given to the scoresheet yet')
                      : t('Pages:ParticipantWeb.ScoresheetPage.feedbackStarPrompt', 'Tap a star to rate')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {new Array(6).fill(0).map((_, index) => {
                const isActive = (hoveredStar !== null ? hoveredStar : grade ?? -1) >= index;
                return (
                  <div
                    key={index}
                    onClick={() => onGradeSelect(index)}
                    onMouseEnter={() => !gradeReadOnly && setHoveredStar(index)}
                    onMouseLeave={() => !gradeReadOnly && setHoveredStar(null)}
                    className={classNames(
                      isActive ? 'icon-[ph--star-fill] text-warning-400' : 'icon-[ph--star] text-content-300 dark:text-content-600',
                      'h-9 w-9 transition-colors duration-150',
                      !gradeReadOnly && 'cursor-pointer hover:text-warning-500',
                    )}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      {/* ── Comments Feed Section ── */}
      <Card>
        <div className="col-span-12 space-y-4">
          <p className="font-sans text-sm font-semibold uppercase tracking-widest text-primary-700 dark:text-primary-300">
            {t('Pages:ParticipantWeb.ScoresheetPage.feedbackCommentLabel', 'Additional Comments')}
          </p>

          <div className="flex flex-col gap-3">
            {sectionComments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center bg-content-50 dark:bg-content-800/20 rounded-2xl border border-content-100 dark:border-content-800/40">
                <div className="icon-[ph--chat-circle-dots] h-10 w-10 text-content-300 dark:text-content-600 mb-2" />
                <p className="font-sans text-sm font-medium text-content-500 dark:text-content-400">
                  {t('Pages:ScoresheetPage.noComments', 'No comments yet')}
                </p>
              </div>
            ) : (
              sectionComments.map((comment: any, idx: number) => {
                const commentUserId = comment?.user?.data?.id || comment?.user?.id || comment?.userId;
                const isMyComment = commentUserId === userId;
                return (
                  <div
                    key={comment?.id || idx}
                    className={classNames(
                      "flex flex-col gap-1 max-w-[85%]",
                      isMyComment ? "self-end items-end" : "self-start items-start"
                    )}
                  >
                    <div
                      className={classNames(
                        "rounded-2xl px-4 py-3 shadow-sm",
                        isMyComment
                          ? "bg-primary-500 text-white rounded-br-sm"
                          : "border border-primary-100/50 bg-primary-50/30 dark:border-primary-800/30 dark:bg-primary-900/10 text-primary-900 dark:text-primary-100 rounded-bl-sm"
                      )}
                    >
                      <p className="font-sans text-sm leading-relaxed whitespace-pre-wrap">
                        {comment?.comment}
                      </p>
                    </div>
                    <div className={classNames("flex items-center gap-2 px-1", isMyComment ? "flex-row-reverse" : "flex-row")}>
                      <span className="font-sans text-xs italic text-content-500 dark:text-content-400">
                        {isMyComment ? t('global:shared.you', 'You') : `${comment?.user?.data?.firstName} ${comment?.user?.data?.lastName}`}
                      </span>
                      <span className="font-sans text-[10px] text-content-400 dark:text-content-500">
                        {dayjs(comment?.insertedAt).format(DATE_FORMAT)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {!readOnly && (
            <div className="pt-2">
              <form
                onSubmit={handleSubmit(onCommentSubmit)}
                className="flex items-end gap-3"
              >
                <textarea
                  {...register('comment')}
                  placeholder={
                    isJudge
                      ? t(
                        'Pages:JudgeWeb.ScoresheetPage.feedbackCommentPlaceholder',
                        'Reply to the participant or add more details...',
                      )
                      : t(
                        'Pages:ParticipantWeb.ScoresheetPage.feedbackCommentPlaceholder',
                        'Share your experience with the judging quality, clarity and communication...',
                      )
                  }
                  rows={2}
                  className="flex-1 resize-none rounded-2xl border border-primary-200 bg-white px-4 py-3 font-sans text-sm text-primary-900 placeholder-content-400 outline-none transition-colors focus:border-primary-400 focus:ring-0 dark:border-primary-700 dark:bg-content-800 dark:text-primary-100 dark:placeholder-content-500 dark:focus:border-primary-600"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(onCommentSubmit)();
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={!commentValue?.trim() || createScoresheetComment.isPending}
                  className={classNames(
                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-all',
                    commentValue?.trim()
                      ? 'bg-primary-500 text-white shadow-md hover:bg-primary-600 active:scale-95'
                      : 'bg-content-100 text-content-400 dark:bg-content-700 dark:text-content-500 cursor-not-allowed',
                  )}
                >
                  {createScoresheetComment.isPending ? (
                    <div className="icon-[ph--spinner] h-5 w-5 animate-spin" />
                  ) : (
                    <div className="icon-[ph--paper-plane-tilt] h-5 w-5" />
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default FeedbackSection;
